import { useQuery } from "@tanstack/react-query";
import type { CcoOverviewResponse } from "@nxvolt/shared";
import PatioMatrix from "../components/PatioMatrix.js";
import { Skeleton } from "../components/Skeleton.js";
import {
  IconBattery,
  IconBell,
  IconBolt,
  IconTower,
  IconTrend,
  IconTruck,
  IconWrench,
} from "../components/icons.js";
import "../styles/cco.css";
import "../styles/patio-charger.css";

type CcoFrota = NonNullable<CcoOverviewResponse["frota"]>;
type CcoEnergia = NonNullable<CcoOverviewResponse["energia"]>;
type CcoOcpp = NonNullable<CcoOverviewResponse["ocpp"]>;
type CcoRelatorios = NonNullable<CcoOverviewResponse["relatorios"]>;
type PatioStatus = CcoOcpp["layoutPatio"][number]["status"];

async function fetchCcoOverview(): Promise<CcoOverviewResponse> {
  const response = await fetch("/api/cco/overview", { credentials: "include" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return (await response.json()) as CcoOverviewResponse;
}

function formatUpdated(iso: string | undefined): string {
  if (!iso) return "Atualizando dados";
  return `Atualizado às ${new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;
}

function formatMinutes(value: number | null): string {
  if (value === null || value < 0) return "—";
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}` : `${minutes} min`;
}

function formatPower(value: number | null): string {
  // Não mostramos potência negativa como carga entregue: a direção é ambígua.
  if (value === null || value < 0) return "—";
  return `${value.toFixed(1)} kW`;
}

function statusCounts(layout: CcoOcpp["layoutPatio"] | undefined): Record<PatioStatus, number> {
  const counts: Record<PatioStatus, number> = { available: 0, charging: 0, alert: 0, offline: 0 };
  for (const item of layout ?? []) counts[item.status] += 1;
  return counts;
}

function StateDot({ tone = "live" }: { tone?: "live" | "warning" | "muted" }) {
  return <span className={`nx-cco-state-dot nx-cco-state-dot--${tone}`} aria-hidden="true" />;
}

function OperationalStat({ label, value, detail, tone, icon }: {
  label: string;
  value: number | string;
  detail: string;
  tone: "navy" | "info" | "success" | "warning";
  icon: JSX.Element;
}) {
  return (
    <article className={`nx-cco-stat nx-cco-stat--${tone}`}>
      <span className="nx-cco-stat__icon">{icon}</span>
      <div>
        <span className="nx-cco-stat__label">{label}</span>
        <strong className="nx-cco-stat__value mono">{value}</strong>
        <span className="nx-cco-stat__detail">{detail}</span>
      </div>
    </article>
  );
}

function PanelHeader({ id, title, subtitle, href, action }: {
  id: string;
  title: string;
  subtitle?: string;
  href?: string;
  action?: string;
}) {
  return (
    <header className="nx-cco-panel__head">
      <div>
        <h2 id={id}>{title}</h2>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {href && action ? <a href={href}>{action}</a> : null}
    </header>
  );
}

export function CcoPage() {
  const { data, error, isLoading } = useQuery<CcoOverviewResponse, Error>({
    queryKey: ["cco", "overview"],
    queryFn: fetchCcoOverview,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const counts = statusCounts(data?.ocpp?.layoutPatio);

  return (
    <div className="nx-cco">
      <header className="nx-cco-hero">
        <div>
          <span className="nx-page-head__eyebrow">Operação em tempo real</span>
          <h1>Centro de Controle Operacional</h1>
          <p>Frota, pátio de recarga e exceções em uma única visão.</p>
        </div>
        <div className="nx-cco-hero__status" role="status">
          <StateDot tone={error ? "warning" : "live"} />
          <span>{error ? "Falha na atualização" : formatUpdated(data?.generatedAt)}</span>
        </div>
      </header>

      {data?.warnings?.length ? (
        <section className="nx-cco-notice" role="status"><IconBell /><span>{data.warnings.join(" · ")}</span></section>
      ) : null}
      {error && !data ? <section className="nx-cco-error" role="alert">Não foi possível carregar o CCO. {error.message}</section> : null}

      <section className="nx-cco-stats" aria-label="Resumo operacional">
        {isLoading || !data?.frota ? Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} variant="kpi" />) : <>
          <OperationalStat label="Frota total" value={data.frota.kpis.total} detail="veículos monitorados" tone="navy" icon={<IconTruck />} />
          <OperationalStat label="Carregando" value={data.frota.kpis.carregando} detail={`${counts.charging} conectores em sessão`} tone="info" icon={<IconBolt />} />
          <OperationalStat label="Concluídas" value={data.frota.kpis.concluida24h} detail="sessões nas últimas 24h" tone="success" icon={<IconBattery />} />
          <OperationalStat label="Manutenção" value={data.frota.kpis.manutencao} detail="ordens abertas ou em curso" tone="warning" icon={<IconWrench />} />
        </>}
      </section>

      <section className="nx-cco-panel nx-cco-panel--fleet" aria-labelledby="cco-fleet-title">
        <PanelHeader id="cco-fleet-title" title="Telemetria da frota" subtitle="Estado atual dos veículos com sinal" href="/app/telemetria_frota" action="Ver telemetria" />
        {isLoading || !data?.frota ? <Skeleton variant="row" count={6} /> : <>
          <div className="nx-cco-table-wrap">
            <table className="nx-cco-table">
              <thead><tr><th>Ônibus</th><th>Linha</th><th>Estado de carga</th><th className="num">SOC</th><th className="num">Previsão*</th><th className="num">Potência</th></tr></thead>
              <tbody>
                {data.frota.telemetria.length ? data.frota.telemetria.slice(0, 12).map((vehicle) => {
                  const soc = vehicle.socPct === null ? null : Math.max(0, Math.min(100, vehicle.socPct));
                  return <tr key={vehicle.prefixo}>
                    <td className="mono nx-cco-table__id">{vehicle.prefixo}</td><td>{vehicle.linha ?? "Sem linha"}</td>
                    <td>{soc === null ? <span className="nx-cco-muted">Sem leitura</span> : <div className="nx-cco-soc" aria-label={`SOC ${soc.toFixed(0)}%`}><span style={{ width: `${soc}%` }} /></div>}</td>
                    <td className="num mono">{soc === null ? "—" : `${soc.toFixed(0)}%`}</td><td className="num mono">{formatMinutes(vehicle.tempoRestanteMin)}</td><td className="num mono">{formatPower(vehicle.potenciaKw)}</td>
                  </tr>;
                }) : <tr><td colSpan={6} className="nx-cco-empty">Nenhum veículo com telemetria disponível.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="nx-cco-footnote">* Previsão calculada a partir do SOC e da potência informada; não representa horário de saída confirmado.</p>
        </>}
      </section>

      <section className="nx-cco-workspace" aria-label="Pátio e exceções operacionais">
        <article className="nx-cco-panel nx-cco-panel--yard" aria-labelledby="cco-yard-title">
          <PanelHeader id="cco-yard-title" title="Pátio de recarga" subtitle="Disponibilidade dos conectores OCPP" href="/app/chargers" action="Abrir carregadores" />
          {isLoading || !data?.ocpp ? <Skeleton variant="row" count={5} /> : <>
            <div className="nx-cco-yard-summary" aria-label="Resumo do pátio">
              <span><StateDot tone="live" /> {counts.available} livres</span><span className="nx-cco-yard-summary__charging"><StateDot tone="muted" /> {counts.charging} carregando</span><span className="nx-cco-yard-summary__alert"><StateDot tone="warning" /> {counts.alert} em alerta</span><span className="nx-cco-yard-summary__offline">{counts.offline} sem sinal</span>
            </div>
            <PatioMatrix layout={data.ocpp.layoutPatio} />
          </>}
        </article>
        <div className="nx-cco-side-stack"><SessionPanel loading={isLoading} data={data?.ocpp} /><AlertsPanel loading={isLoading} data={data?.ocpp} /></div>
      </section>

      <section className="nx-cco-lower-grid"><EnergyPanel loading={isLoading} data={data?.energia} /><UtilizationPanel loading={isLoading} data={data?.relatorios} /></section>
    </div>
  );
}

function SessionPanel({ loading, data }: { loading: boolean; data: CcoOcpp | null | undefined }) {
  const session = data?.sessaoSelecionada;
  return <article className="nx-cco-panel nx-cco-panel--session" aria-labelledby="cco-session-title">
    <PanelHeader id="cco-session-title" title="Sessão em foco" subtitle={session ? `${session.cpId} · conector ${session.connectorId}` : "Sem medições disponíveis"} />
    {loading ? <Skeleton variant="row" count={3} /> : !session ? <p className="nx-cco-empty">Nenhuma sessão com medições disponíveis para detalhar.</p> : <dl className="nx-cco-session-grid">
      <div><dt>Energia medida</dt><dd>{session.kwhInjetado === null ? "—" : `${session.kwhInjetado.toFixed(1)} kWh`}</dd></div>
      <div><dt>Tensão estimada</dt><dd>{session.voltageV === null ? "—" : `${session.voltageV} V`}</dd></div>
      <div><dt>Corrente estimada</dt><dd>{session.currentA === null ? "—" : `${session.currentA} A`}</dd></div>
      <div><dt>Temperatura da bateria</dt><dd>{session.cellTempC === null ? "Sem leitura" : `${session.cellTempC.toFixed(1)} °C`}</dd></div>
    </dl>}
  </article>;
}

function AlertsPanel({ loading, data }: { loading: boolean; data: CcoOcpp | null | undefined }) {
  return <article className="nx-cco-panel nx-cco-panel--alerts" aria-labelledby="cco-alerts-title">
    <PanelHeader id="cco-alerts-title" title="Alertas e manutenção" subtitle="Ocorrências abertas mais recentes" href="/app/alerts" action="Ver alertas" />
    {loading ? <Skeleton variant="row" count={3} /> : !data?.alertas.length ? <p className="nx-cco-empty"><StateDot tone="live" /> Nenhum alerta ativo.</p> : <ul className="nx-cco-alert-list">
      {data.alertas.slice(0, 4).map((alert) => <li key={alert.id} className={`nx-cco-alert nx-cco-alert--${alert.severity}`}><span className="mono">{alert.cpId}</span><p>{alert.message}</p></li>)}
    </ul>}
  </article>;
}

function EnergyPanel({ loading, data }: { loading: boolean; data: CcoEnergia | null | undefined }) {
  const demo = data?.source === "demo";
  return <article className="nx-cco-panel nx-cco-panel--energy" aria-labelledby="cco-energy-title">
    <PanelHeader id="cco-energy-title" title="Energia e demanda" subtitle={demo ? "Fonte de medição pendente" : "Leitura da subestação"} href="/app/energia_demanda" action="Ver demanda" />
    {loading || !data ? <Skeleton variant="row" count={3} /> : <>
      {demo ? <p className="nx-cco-source-note"><IconTower /> Dados de demonstração. O medidor Kron ainda não está conectado.</p> : null}
      <div className="nx-cco-energy-values"><div><span>Demanda contratada</span><strong>{data.demandaContratadaKw} kW</strong></div><div><span>Janela de ponta</span><strong>{data.horarioPontaInicio}–{data.horarioPontaFim}</strong></div><div><span>Estratégia ativa</span><strong className="nx-cco-capitalize">{data.estrategiaAtual}</strong></div></div>
    </>}
  </article>;
}

function UtilizationPanel({ loading, data }: { loading: boolean; data: CcoRelatorios | null | undefined }) {
  return <article className="nx-cco-panel nx-cco-panel--utilization" aria-labelledby="cco-utilization-title">
    <PanelHeader id="cco-utilization-title" title="Uso dos carregadores" subtitle="Proxy operacional nas últimas 24 horas" href="/app/heatmap_utilizacao" action="Abrir heatmap" />
    {loading || !data ? <Skeleton variant="row" count={3} /> : <>
      <div className="nx-cco-utilization-head"><IconTrend /><div><span>Utilização estimada</span><strong>{data.eficienciaMediaPct == null ? '—' : `${data.eficienciaMediaPct.toFixed(0)}%`}</strong></div></div>
      <div className="nx-cco-utilization-list">{data.eficienciaPorCp.slice(0, 4).map((charger) => <div key={charger.cpId}><span className="mono">{charger.cpId}</span><span className="nx-cco-utilization-bar"><i style={{ width: `${Math.max(0, Math.min(100, charger.eficienciaPct ?? 0))}%` }} /></span><strong className="mono">{charger.eficienciaPct == null ? '—' : `${charger.eficienciaPct.toFixed(0)}%`}</strong></div>)}</div>
      <p className="nx-cco-footnote">Estimativa de energia por hora de sessão, usando potência nominal de referência. Não é eficiência elétrica certificada.</p>
    </>}
  </article>;
}
